import { Box, Button, Link, Typography } from "@mui/material";
import React from "react";
import useWindowSize from "../../../hooks/useWindowSize";
import useTourDates from "../../../hooks/useTourDates";
import { trackClick } from "../../_helpers";

const CalendarIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M38 6H34V4C34 2.9 33.1 2 32 2C30.9 2 30 2.9 30 4V6H18V4C18 2.9 17.1 2 16 2C14.9 2 14 2.9 14 4V6H10C7.8 6 6.02 7.8 6.02 10L6 42C6 44.2 7.8 46 10 46H38C40.2 46 42 44.2 42 42V10C42 7.8 40.2 6 38 6ZM38 42H10V16H38V42ZM14 22H18V26H14V22ZM22 22H26V26H22V22ZM30 22H34V26H30V22Z"
      fill="var(--logoColor2)"
    />
  </svg>
);

export function TourDatesSection(props) {
  const { artistData } = props;
  const windowSize = useWindowSize();

  // Fetch tour dates from Bandsintown
  const { data: tourData, loading, error } = useTourDates(artistData?.name, [artistData?.name]);

  if (!artistData || loading) {
    return null;
  }

  const hasEvents = tourData && tourData.events && tourData.events.length > 0;

  // Hide section entirely if there are no events
  if (!hasEvents) {
    return null;
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box
      className="TourDatesSection"
      sx={{
        position: "relative",
        width: `calc(${windowSize.width}px - (2.5rem * 2))`,
        p: { xs: "4rem 2rem", md: "3.5rem" },
        m: { xs: "1rem 2.5rem 0", md: "12.25rem 2.5rem 0" },
        backgroundColor: "var(--color5)",
        borderRadius: "1.875rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: { xs: "2.5rem", md: "2.5rem" },
        zIndex: 1,
      }}
    >
      {/* Header */}
      <Box
        className="TourHeader"
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: { xs: "2rem", md: "1rem" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "1rem", md: "1.5rem" },
          }}
        >
          <CalendarIcon />
          <Typography
            className="TourDates"
            sx={{
              color: "var(--logoColor2)",
              fontSize: { xs: "3rem", md: "3.34rem" },
              fontFamily: "Hanson",
              fontWeight: "700",
            }}
          >
            TOUR DATES
          </Typography>
        </Box>

        {hasEvents && (
          <Typography
            sx={{
              color: "var(--logoColor)",
              fontSize: { xs: "1.125rem", md: "0.875rem" },
              fontFamily: "Poppins",
              fontWeight: "400",
            }}
          >
            {tourData.events.length} upcoming {tourData.events.length === 1 ? "show" : "shows"}
          </Typography>
        )}
      </Box>

      {/* Events List */}
      {hasEvents ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: { xs: "1.5rem", md: "1.25rem" },
          }}
        >
          {tourData.events.map((event, index) => (
          <Box
            key={event.id || index}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: "1rem", md: "2rem" },
              p: { xs: "1.5rem", md: "1.5rem" },
              backgroundColor: "var(--color4)",
              borderRadius: "0.75rem",
              border: "1px solid var(--logoColor2)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              },
            }}
          >
            {/* Date */}
            <Box
              sx={{
                minWidth: { xs: "100%", md: "150px" },
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem",
              }}
            >
              <Typography
                sx={{
                  color: "var(--logoColor2)",
                  fontSize: { xs: "1.5rem", md: "1.25rem" },
                  fontFamily: "Hanson",
                  fontWeight: "700",
                }}
              >
                {formatDate(event.datetime)}
              </Typography>
              <Typography
                sx={{
                  color: "var(--logoColor)",
                  fontSize: { xs: "1.125rem", md: "0.875rem" },
                  fontFamily: "Poppins",
                  fontWeight: "400",
                }}
              >
                {formatTime(event.datetime)}
              </Typography>
            </Box>

            {/* Venue Info */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <Typography
                sx={{
                  color: "var(--logoColor2)",
                  fontSize: { xs: "1.375rem", md: "1.125rem" },
                  fontFamily: "Poppins",
                  fontWeight: "600",
                }}
              >
                {event.venue.name}
              </Typography>
              <Typography
                sx={{
                  color: "var(--logoColor)",
                  fontSize: { xs: "1.125rem", md: "0.875rem" },
                  fontFamily: "Poppins",
                  fontWeight: "400",
                }}
              >
                {event.venue.city}, {event.venue.region || event.venue.country}
              </Typography>
              {event.lineup && event.lineup.length > 1 && (
                <Typography
                  sx={{
                    color: "var(--logoColor)",
                    fontSize: { xs: "1rem", md: "0.75rem" },
                    fontFamily: "Poppins",
                    fontWeight: "400",
                    fontStyle: "italic",
                  }}
                >
                  with {event.lineup.filter(a => a !== artistData.name).join(', ')}
                </Typography>
              )}
            </Box>

            {/* Tickets Button */}
            {event.url && (
              <Link
                href={event.url}
                onClick={() => trackClick("tour-tickets", { label: event.venue?.name || "Tickets", section: "tour", href: event.url })}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none" }}
              >
                <Button
                  sx={{
                    padding: { xs: "1rem 2rem", md: "0.75rem 1.5rem" },
                    backgroundColor: "var(--logoColor2)",
                    color: "var(--color5)",
                    fontSize: { xs: "1.125rem", md: "0.875rem" },
                    fontFamily: "Hanson",
                    fontWeight: "700",
                    borderRadius: "0.5rem",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "var(--logoColor)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Get Tickets
                </Button>
              </Link>
            )}
          </Box>
        ))}
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            padding: { xs: "3rem 2rem", md: "3rem 2rem" },
            textAlign: "center",
            backgroundColor: "var(--color4)",
            borderRadius: "1rem",
          }}
        >
          <Typography
            sx={{
              color: "var(--logoColor)",
              fontSize: { xs: "1.25rem", md: "1rem" },
              fontFamily: "Poppins",
              fontWeight: "400",
              lineHeight: 1.6,
            }}
          >
            {error
              ? `No tour dates available at this time. Check back soon for ${artistData.name}'s upcoming shows!`
              : `${artistData.name} doesn't have any tour dates scheduled yet. Follow them on social media for the latest updates!`
            }
          </Typography>
        </Box>
      )}

      {/* Powered by */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          mt: { xs: "1rem", md: "1rem" },
        }}
      >
        <Typography
          sx={{
            color: "var(--logoColor)",
            fontSize: { xs: "1.125rem", md: "0.875rem" },
            fontFamily: "Poppins",
            fontWeight: "400",
            textAlign: "center",
          }}
        >
          Powered by Bandsintown
        </Typography>
      </Box>
    </Box>
  );
}
